/**
 * services/schedules.js — Schedules domain (per-item versioned, Schedule Builder, Round 18).
 *
 * Data source: Supabase schedule_items + schedule_item_versions + schedule_substitution_requests.
 * All writes go through /api/schedules (Clerk team auth).
 */

import { apiGet } from './_api.js'

const API = '/api/schedules'
const TBL_ROOMS = 'tbl7sWrKq7bmvctSx'
const TBL_DEL = 'tblFuiDpahqCL4eIK'   // Project Deliverables (milestones)

/** Upload an image for a schedule item to Supabase Storage and return its public URL.
 *  Caller then persists it onto the item (attrs['Image URL']) via updateScheduleItem. */
export async function uploadItemImage(itemId, file) {
  const token = await window.__clerkGetToken()
  const credRes = await fetch(API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: JSON.stringify({ action: 'imageUploadUrl', itemId, filename: file.name }),
  })
  const cred = await credRes.json().catch(() => ({}))
  if (!credRes.ok) throw new Error(cred.error || ('Could not get upload URL (' + credRes.status + ')'))
  const up = await fetch(cred.signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream', 'x-upsert': 'true' },
    body: file,
  })
  if (!up.ok) throw new Error('Storage upload failed (' + up.status + ')')
  return cred.publicUrl
}

/** Resolve a milestone's owning project record id straight from its Airtable record — the
 *  bulletproof source when the caller can't supply projectId (any outline view). Returns recXXX|null. */
export function getMilestoneProjectId(milestoneId) {
  if (!milestoneId) return Promise.resolve(null)
  return apiGet(TBL_DEL + '/' + milestoneId, '').then(rec => {
    const f = (rec && rec.fields) || {}
    const p = f['Project'] || f['Project Record UID from Project'] || f['Project Record UID (from Project)']
    return Array.isArray(p) ? (p[0] || null) : (p || null)
  }).catch(() => null)
}

/** Rooms for a concept — for the schedule-item room picker. */
export function getConceptRooms(conceptId) {
  if (!conceptId) return Promise.resolve([])
  const formula = 'FIND("' + conceptId + '",ARRAYJOIN({Record UID (from Project Concept)},","))'
  const qs = 'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=300'
  return apiGet(TBL_ROOMS, qs).then(recs => (recs || []).map(r => {
    const f = r.fields || {}
    return {
      id: r.id,
      label: f['Room'] || f['Room Number and Name'] || f['Custom Name'] || r.id,
      roomString: f['Room Number and Name'] || f['Room'] || f['Custom Name'] || '',
    }
  }).sort((a, b) => (a.label < b.label ? -1 : a.label > b.label ? 1 : 0)))
}

async function call(method, body, qs) {
  const token = await window.__clerkGetToken()
  const url = API + (qs ? '?' + qs : '')
  const res = await fetch(url, {
    method,
    headers: method === 'GET'
      ? { 'Authorization': 'Bearer ' + token }
      : { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: method === 'GET' ? undefined : JSON.stringify(body),
  })
  const j = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(j.error || 'schedules error: ' + res.status)
  return j
}

/** All live schedule items for a project (+ concept), ordered by type then sort_order. */
export function getScheduleItems(projectId, conceptId) {
  const qs = 'projectId=' + encodeURIComponent(projectId) + (conceptId ? '&conceptId=' + encodeURIComponent(conceptId) : '')
  return call('GET', null, qs).then(d => d.schedules || [])
}

/** Version history for a specific schedule item (newest first). */
export function getItemVersions(itemId) {
  return call('GET', null, 'what=versions&itemId=' + encodeURIComponent(itemId)).then(d => d.versions || [])
}

// (No bare createScheduleItem — items are only created inside a milestone via createItemInMilestone,
// so the schedule never has orphan items not tied to a milestone.)

/** Update a schedule item's editable fields (the live working state). */
export function updateScheduleItem(id, fields) {
  return call('POST', { action: 'updateScheduleItem', id, fields }).then(d => d.item)
}

export function deleteScheduleItem(id) {
  return call('POST', { action: 'deleteScheduleItem', id }).then(d => d.ok)
}

export function reorderScheduleItems(order) {
  return call('POST', { action: 'reorderScheduleItems', order }).then(d => d.ok)
}

// ── Milestone membership (the spec drawing-sheet model) ───────────────────────

/** Items that belong to a milestone (have a version there). Each row = item fields + `version`
 *  (the milestone's version row). Editable when `version.status === 'open'`. */
export function getMilestoneScheduleItems(milestoneId) {
  if (!milestoneId) return Promise.resolve([])
  return call('GET', null, 'what=milestoneItems&milestoneId=' + encodeURIComponent(milestoneId)).then(d => d.items || [])
}

/** Issued version rows at a milestone (read-only display under the milestone). */
export function getMilestoneScheduleVersions(milestoneId) {
  if (!milestoneId) return Promise.resolve([])
  return call('GET', null, 'what=milestoneVersions&milestoneId=' + encodeURIComponent(milestoneId)).then(d => d.versions || [])
}

/** Every concept item with its full version chain — candidate list + roll-up + orphan flag. */
export function getScheduleItemsWithVersions(projectId, conceptId) {
  const qs = 'what=itemsWithVersions&projectId=' + encodeURIComponent(projectId) +
    (conceptId ? '&conceptId=' + encodeURIComponent(conceptId) : '')
  return call('GET', null, qs).then(d => d.items || [])
}

/** Create a NEW item directly in a milestone (Original Release open version). */
/** Create a LIVE working item with no version row (AlderSPEC Phase 4 — membership comes later
 *  via addItemToMilestone when the item is sent into a release). */
export function createScheduleItem(projectId, conceptAirtableId, fields) {
  return call('POST', { action: 'createScheduleItem', projectId, conceptAirtableId: conceptAirtableId || null, fields })
    .then(d => d.item)
}

export function createItemInMilestone(projectId, conceptAirtableId, milestone, fields) {
  return call('POST', {
    action: 'createItemInMilestone', projectId, conceptAirtableId: conceptAirtableId || null,
    milestoneId: milestone.id, milestoneName: milestone.name || null, mode: milestone.mode || null, fields,
  }).then(d => ({ item: d.item, version: d.version }))
}

/** Add an existing item to a milestone (pull forward). Creates an open version. */
export function addItemToMilestone(scheduleItemId, milestone) {
  return call('POST', {
    action: 'addItemToMilestone', scheduleItemId,
    milestoneId: milestone.id, milestoneName: milestone.name || null, mode: milestone.mode || null,
  }).then(d => ({ version: d.version, already: d.already }))
}

/** Remove an item from a milestone (undo a pull). Open versions only. */
export function removeItemFromMilestone(versionId) {
  return call('POST', { action: 'removeItemFromMilestone', versionId }).then(d => d.ok)
}

/** Issue a milestone's schedule — freeze every open version at the milestone into a snapshot.
 *  Returns { issued, items }. Called on milestone post. */
export function issueMilestoneSchedule(projectId, milestone) {
  return call('POST', {
    action: 'issueMilestoneSchedule', projectId,
    milestoneId: milestone.id, milestoneName: milestone.name || null, mode: milestone.mode || 'full',
  }).then(d => ({ issued: d.issued, items: d.items || [] }))
}

/** Log an internal substitution request for a schedule item. */
export function logSubstitution(scheduleItemId, proposed, reason) {
  return call('POST', { action: 'logSubstitution', scheduleItemId, proposed, reason }).then(d => d.substitution)
}

/** Approve (applies proposed to the live item) or reject a substitution request. */
export function reviewSubstitution(id, decision, note) {
  return call('POST', { action: 'reviewSubstitution', id, decision, note }).then(d => d.substitution)
}

/** All substitution requests for a project (+ concept), newest first. */
export function getScheduleSubstitutions(projectId, conceptId) {
  const qs = 'what=substitutions&projectId=' + encodeURIComponent(projectId) + (conceptId ? '&conceptId=' + encodeURIComponent(conceptId) : '')
  return call('GET', null, qs).then(d => d.substitutions || [])
}

/** Client deliverable-approval rows for this project's issued schedule versions. */
export function getScheduleApprovals(projectId) {
  return call('GET', null, 'what=approvals&projectId=' + encodeURIComponent(projectId)).then(d => d.approvals || [])
}

/**
 * Generate the standalone schedule PDF (all types, grouped per the shared layout).
 * Mirrors exportSpecPdf: with opts.preview/opts.targetWindow it opens the PDF in a new tab instead
 * of downloading (the tab should be pre-opened inside the click to dodge popup blockers).
 */
export async function exportSchedulePdf(projectId, conceptId, projectName, opts = {}) {
  const token = await window.__clerkGetToken()
  const res = await fetch('/api/schedule-pdf', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: JSON.stringify({ projectId, conceptId, projectName, milestoneId: opts.milestoneId || null, previewMilestoneId: opts.previewMilestoneId || null }),
  })
  if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'PDF failed: ' + res.status) }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  // Preview: point a (pre-opened, to dodge popup blockers) tab at the PDF instead of downloading.
  if (opts.targetWindow || opts.preview) {
    try {
      if (opts.targetWindow && !opts.targetWindow.closed) opts.targetWindow.location.href = url
      else window.open(url, '_blank')
    } catch { window.open(url, '_blank') }
    // Keep the blob alive long enough to view/reload — Chrome's PDF viewer re-fetches the blob on
    // reload/scroll, and revoking too early gives "ERR_FILE_NOT_FOUND". 30 min is plenty.
    setTimeout(() => URL.revokeObjectURL(url), 1800000)
    return
  }
  const a = document.createElement('a')
  a.href = url
  a.download = (projectName || 'Schedules').replace(/[^\w]+/g, '_') + '_Schedules.pdf'
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 4000)
}

/**
 * PROJECT STYLE phase 5 (2026-10-07): sync the model palette's rows (kernel
 * paletteScheduleRows) into the project's AlderSPEC Colors schedule. Owns ONLY the rows it made
 * (attrs.paletteRole) — a row someone typed is never touched. A palette row is updated in place
 * (its other attrs, e.g. an image, kept); a role no longer in the palette loses its row, unless
 * that row was already sent into a milestone, which keeps it (history stays whole).
 * Returns { created, updated, removed, kept, unchanged }.
 */
export async function syncPaletteColorsSchedule(projectId, conceptId, rows) {
  const items = await getScheduleItemsWithVersions(projectId, conceptId || null)
  const mine = new Map()
  for (const it of items) {
    const role = it.schedule_type === 'Colors' && it.attrs && it.attrs.paletteRole
    if (role && !mine.has(role)) mine.set(role, it)
  }
  const out = { created: 0, updated: 0, removed: 0, kept: 0, unchanged: 0 }
  const want = new Set(rows.map((r) => r.role))
  for (const r of rows) {
    const it = mine.get(r.role)
    if (!it) {
      await createScheduleItem(projectId, conceptId || null, { schedule_type: 'Colors', item_label: r.item_label, spec_section: r.spec_section, attrs: r.attrs })
      out.created++
      continue
    }
    const attrs = { ...(it.attrs || {}), ...r.attrs }
    const same = it.item_label === r.item_label && it.spec_section === r.spec_section
      && Object.keys(r.attrs).every((k) => (it.attrs || {})[k] === r.attrs[k])
    if (same) { out.unchanged++; continue }
    await updateScheduleItem(it.id, { item_label: r.item_label, spec_section: r.spec_section, attrs })
    out.updated++
  }
  for (const [role, it] of mine) {
    if (want.has(role)) continue
    if ((it.versions || []).length) { out.kept++; continue }
    await deleteScheduleItem(it.id)
    out.removed++
  }
  return out
}
